import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useComputedColorScheme } from '@mantine/core';
import type { AnimationItem } from 'lottie-web';
import './Logo.css';

type LottieColor = [number, number, number, number];

const DARK_MAIN_BLUE: LottieColor = [0.133333333333, 0.545098039216, 0.901960784314, 1];
const DARK_GRAY: LottieColor = [0.423529411765, 0.458823529412, 0.490196078431, 1];

const DARK_MAIN_BLUE_NEW: LottieColor = [0.5, 0.8, 1, 1];
const DARK_GRAY_NEW: LottieColor = [0.92, 0.92, 0.92, 1];

function tweakDarkLogoColors(data: any): any {
  const nearlyEqual = (a: number, b: number) => Math.abs(a - b) < 0.001;

  const matchesColor = (arr: unknown, target: LottieColor): arr is LottieColor =>
    Array.isArray(arr) &&
    arr.length === 4 &&
    arr.every(
      (value, index) =>
        typeof value === 'number' && nearlyEqual(value, target[index]),
    );

  const mapColorArray = (arr: unknown): unknown => {
    if (!Array.isArray(arr)) return arr;
    if (matchesColor(arr, DARK_MAIN_BLUE)) return [...DARK_MAIN_BLUE_NEW];
    if (matchesColor(arr, DARK_GRAY)) return [...DARK_GRAY_NEW];
    return arr;
  };

  const walk = (obj: any): void => {
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
      obj.forEach(walk);
      return;
    }

    if (obj.c && obj.c.k) {
      const k = obj.c.k;
      if (Array.isArray(k)) {
        if (k.length > 0 && typeof k[0] === 'number') {
          obj.c.k = mapColorArray(k);
        } else {
          obj.c.k = k.map((value: unknown) =>
            Array.isArray(value) ? mapColorArray(value) : value,
          );
        }
      }
    }

    Object.values(obj).forEach(walk);
  };

  const cloned = JSON.parse(JSON.stringify(data));
  walk(cloned);
  return cloned;
}

let baseLogoData: any | null = null;
let baseLogoDataPromise: Promise<any> | null = null;

async function getBaseLogoData(): Promise<any> {
  if (baseLogoData) {
    return baseLogoData;
  }

  if (!baseLogoDataPromise) {
    baseLogoDataPromise = fetch('/logo.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load logo.json');
        }
        return response.json();
      })
      .then((data) => {
        baseLogoData = data;
        return data;
      })
      .catch((error) => {
        baseLogoDataPromise = null;
        throw error;
      });
  }

  return baseLogoDataPromise;
}

interface LogoProps {
  animated?: boolean;
  variant?: 'default' | 'inline';
  className?: string;
  onAnimationComplete?: () => void;
}

export function Logo({
  animated = false,
  variant = 'inline',
  className,
  onAnimationComplete,
}: LogoProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const staticFrameHandlerRef = useRef<(() => void) | null>(null);
  const completeHandlerRef = useRef<(() => void) | null>(null);
  const colorScheme = useComputedColorScheme('light');

  const cropInlineSvgBounds = () => {
    if (variant !== 'inline' || animated || !containerRef.current) {
      return;
    }

    const svg = containerRef.current.querySelector('svg');
    if (!svg) {
      return;
    }

    try {
      const bbox = svg.getBBox();
      if (bbox.width > 0 && bbox.height > 0) {
        const leftTrim = bbox.width * 0.11;
        const rightTrim = 0;
        const topTrim = bbox.height * 0.67;
        const trimmedWidth = Math.max(bbox.width - leftTrim - rightTrim, bbox.width * 0.5);
        const trimmedHeight = Math.max(bbox.height - topTrim, bbox.height * 0.2);
        const viewBoxX = bbox.x + leftTrim;
        const viewBoxY = bbox.y + topTrim;
        svg.setAttribute('viewBox', `${viewBoxX} ${viewBoxY} ${trimmedWidth} ${trimmedHeight}`);
        svg.removeAttribute('width');
        svg.removeAttribute('height');

        // Ensure the inline SVG itself defines the visual height instead of leaving excess space
        svg.style.height = '1em';
        svg.style.width = 'auto';
        svg.style.display = 'block';

        // Allow the canvas wrapper to shrink-wrap to the SVG
        if (containerRef.current) {
          const canvasEl = containerRef.current as HTMLSpanElement;
          canvasEl.style.height = 'auto';
          canvasEl.style.width = 'auto';
          canvasEl.style.overflow = 'visible';
          canvasEl.style.visibility = 'visible';
        }
      }
    } catch {
      // getBBox can throw if the SVG isn't ready; ignore and keep default sizing.
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const loadAnimation = async () => {
      if (!containerRef.current) {
        return;
      }

      if (animationRef.current) {
        if (staticFrameHandlerRef.current) {
          animationRef.current.removeEventListener('DOMLoaded', staticFrameHandlerRef.current);
          animationRef.current.removeEventListener('data_ready', staticFrameHandlerRef.current);
          staticFrameHandlerRef.current = null;
        }
        if (completeHandlerRef.current) {
          animationRef.current.removeEventListener('complete', completeHandlerRef.current);
          completeHandlerRef.current = null;
        }
        animationRef.current.destroy();
        animationRef.current = null;
      }

      try {
        // lottie-web is ~250 KB minified - by far the heaviest thing the shell would otherwise pull
        // in eagerly. The logo already renders asynchronously (it waits on the logo.json fetch), so
        // loading the player in parallel with the data keeps it out of the critical bundle for free.
        const [rawData, lottieModule] = await Promise.all([
          getBaseLogoData(),
          import('lottie-web'),
        ]);
        const lottie = lottieModule.default;
        const animationData =
          colorScheme === 'dark' ? tweakDarkLogoColors(rawData) : rawData;

        if (isCancelled || !containerRef.current) {
          return;
        }

        const animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          // We never loop the logo; when animated is true we play the intro once
          loop: false,
          autoplay: animated,
          animationData,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
          },
        });

        if (!animated) {
          const showFinalFrame = () => {
            if (!animation) return;
            const totalFrames =
              (animation as AnimationItem & { animationData?: { op?: number } }).animationData?.op ??
              animation.getDuration(true);
            animation.goToAndStop(totalFrames, true);
            cropInlineSvgBounds();
          };

          staticFrameHandlerRef.current = showFinalFrame;
          animation.addEventListener('DOMLoaded', showFinalFrame);
          animation.addEventListener('data_ready', showFinalFrame);
        } else if (animated && onAnimationComplete) {
          const handleComplete = () => {
            onAnimationComplete();
          };

          completeHandlerRef.current = handleComplete;
          animation.addEventListener('complete', handleComplete);
        }

        animationRef.current = animation;
      } catch {
      }
    };

    void loadAnimation();

    return () => {
      isCancelled = true;
      if (animationRef.current) {
        if (staticFrameHandlerRef.current) {
          animationRef.current.removeEventListener('DOMLoaded', staticFrameHandlerRef.current);
          animationRef.current.removeEventListener('data_ready', staticFrameHandlerRef.current);
          staticFrameHandlerRef.current = null;
        }
        if (completeHandlerRef.current) {
          animationRef.current.removeEventListener('complete', completeHandlerRef.current);
          completeHandlerRef.current = null;
        }
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [colorScheme, animated, variant, onAnimationComplete]);

  const classes = ['logo-animation'];
  if (variant === 'inline') {
    classes.push('logo-animation--inline');
  } else {
    classes.push('logo-animation--default');
  }
  if (className) {
    classes.push(className);
  }

  return (
    <span className={classes.join(' ')} aria-label={t('brand.logoAria')} role="img">
      <span ref={containerRef} className="logo-animation-canvas" />
    </span>
  );
}
